<template>
    <div>
        <!-- Maintenance Requests List (show if configuration exists) -->
        <div v-if="canViewRequests">
            <div class="flex justify-end items-center mb-6 flex-wrap gap-2">
                <div class="flex items-center gap-2">
                    <button 
                        type="button"
                        @click="showConfigurationForm = !showConfigurationForm"
                        :disabled="isSharedViewOnly"
                        class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-rent-risk-blue-400 hover:bg-rent-risk-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400 disabled:pointer-events-none disabled:opacity-50"
                    >
                        <font-awesome-icon :icon="showConfigurationForm ? faEye : faPencil" class="mr-2" />
                        {{ showConfigurationForm ? 'View Requests' : (hasTenantInvited ? 'Edit Maintenance' : 'Invite Tenant') }}
                    </button>
                    <ToolTip
                        v-if="isSharedViewOnly && viewOnlyTooltip"
                        :title="viewOnlyTooltip.title"
                        :description="viewOnlyTooltip.description"
                    />
                </div>
            </div>

            <HowItWorksBanner
                v-if="!showConfigurationForm"
                description="Follow these steps to manage maintenance requests from start to finish."
                :steps="howItWorksSteps"
                storage-key="maintenance_how_it_works_dismissed"
            />

            <!-- Stats Cards -->
            <div v-if="!showConfigurationForm" class="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div class="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4">
                    <div class="w-10 h-10 shrink-0 rounded-full bg-blue-50 flex items-center justify-center">
                        <font-awesome-icon :icon="faWrench" class="text-rent-risk-blue-400" />
                    </div>
                    <div>
                        <p class="text-xs text-gray-500 font-medium">Total Requests</p>
                        <p class="text-2xl font-bold text-gray-900 leading-tight">{{ requestStats.total }}</p>
                        <p class="text-xs text-gray-400">All time</p>
                    </div>
                </div>
                <div class="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4">
                    <div class="w-10 h-10 shrink-0 rounded-full bg-orange-50 flex items-center justify-center">
                        <font-awesome-icon :icon="faClock" class="text-orange-400" />
                    </div>
                    <div>
                        <p class="text-xs text-gray-500 font-medium">In Progress</p>
                        <p class="text-2xl font-bold text-gray-900 leading-tight">{{ requestStats.in_progress }}</p>
                        <p v-if="requestStats.in_progress > 0" class="text-xs text-orange-500 font-medium">Needs attention</p>
                    </div>
                </div>
                <div class="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4">
                    <div class="w-10 h-10 shrink-0 rounded-full bg-green-50 flex items-center justify-center">
                        <font-awesome-icon :icon="faCheckCircle" class="text-green-500" />
                    </div>
                    <div>
                        <p class="text-xs text-gray-500 font-medium">Completed</p>
                        <p class="text-2xl font-bold text-gray-900 leading-tight">{{ requestStats.completed }}</p>
                        <p class="text-xs text-green-600">This month</p>
                    </div>
                </div>
                <div class="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4">
                    <div class="w-10 h-10 shrink-0 rounded-full bg-purple-50 flex items-center justify-center">
                        <font-awesome-icon :icon="faBoxArchive" class="text-purple-400" />
                    </div>
                    <div>
                        <p class="text-xs text-gray-500 font-medium">Archived</p>
                        <p class="text-2xl font-bold text-gray-900 leading-tight">{{ requestStats.archived }}</p>
                        <p class="text-xs text-gray-400">Hidden from view</p>
                    </div>
                </div>
            </div>

            <!-- Configuration Form (collapsible) -->
            <div v-show="showConfigurationForm" class="mb-8">
                <form @submit.prevent="saveMaintenanceConfiguration" class="space-y-7 bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <fieldset :disabled="isSharedViewOnly" class="contents">
                    <h3 class="text-lg font-medium text-gray-900 border-b pb-2">Edit Maintenance Configuration</h3>
                    
                    <!-- Tenant Maintenance Instructions -->
                    <div>
                        <div class="sm:col-span-6">
                            <h4 class="text-base leading-6 font-medium text-gray-900">
                                Tenant Maintenance Instructions
                            </h4>
                            <p class="text-xs leading-6 font-medium text-gray-500">These are the instructions your tenant will see on their dashboard once you invite them to the portal.</p>
                        </div>

                        <div class="mt-7 grid grid-cols-1 gap-y-12 gap-x-4 sm:grid-cols-6">
                            <div class="sm:col-span-2">
                                <label for="tenant_email"
                                    :class="(form.errors.tenant_email)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Tenant Email
                                </label>
                                <div class="mt-1 relative rounded-md">
                                    <div
                                        class="absolute left-0 pl-3 flex items-center pointer-events-none"
                                        :class="(form.errors.tenant_email)?'top-3':'inset-y-0'">
                                        <font-awesome-icon :icon="faEnvelope" class="text-gray-400" />
                                    </div>
                                    <input id="tenant_email" v-model="form.tenant_email" type="email"
                                        :class="(form.errors.tenant_email)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'"
                                    >
                                    <p v-text="form.errors.tenant_email" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="tenant_message" :class="(form.errors.tenant_message)?'block text-sm inline-flex font-medium text-red-500':'block text-sm inline-flex font-medium text-gray-700'">
                                Email sent to Tenant
                                    <ToolTip
                                        :title="'Email sent to Tenant'"
                                        :description="'This is the message you want your tenant to receive once they put in a request. The below message is the default message.'"
                                    ></ToolTip>
                                </label>
                                <div class="mt-1">
                                    <textarea
                                        id="tenant_message"
                                        rows="4" v-model="form.tenant_message"
                                        :error="form.errors.tenant_message"
                                        placeholder="Custom message received by tenant.."
                                        :class="(form.errors.tenant_message)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'"
                                    >
                                    </textarea>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="repair_amount" :class="(form.errors.repair_amount)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Tenant is responsible for any repairs up to ($)
                                </label>
                                <div class="mt-1 relative rounded-md">
                                    <input id="repair_amount" v-model="form.repair_amount" type="number" step="0.01" :class="(form.errors.repair_amount)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                                    <p v-text="form.errors.repair_amount" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="specific_instructions" :class="(form.errors.specific_instructions)?'block text-sm inline-flex font-medium text-red-500':'block text-sm inline-flex font-medium text-gray-700'">
                                    Specific Instructions
                                    <ToolTip
                                        :title="'Specific Instructions'"
                                        :description="'This is where you tell your tenant how you want them to handle the maintenance request. For example, Call Home Warranty Company for plumbing or electrical issues.'"
                                    ></ToolTip>
                                </label>
                                <div class="mt-1">
                                    <textarea
                                        id="specific_instructions"
                                        rows="2" v-model="form.specific_instructions"
                                        :error="form.errors.specific_instructions"
                                        placeholder="Any other specific instructions for the tenant"
                                        :class="(form.errors.specific_instructions)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:ring-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'"
                                    >
                                    </textarea>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="guidelines" :class="(form.errors.guidelines)?'block text-sm inline-flex font-medium text-red-500':'block text-sm inline-flex font-medium text-gray-700'">
                                    Emergency Guidelines
                                    <ToolTip
                                        :title="'Emergency Guidelines'"
                                        :description="'Please call landlord directly if there is any water emergency such as a leaking roof, flooding basement, etc.'"
                                    ></ToolTip>
                                </label>
                                <div class="mt-1">
                                    <textarea
                                        id="guidelines"
                                        rows="2" v-model="form.guidelines"
                                        :error="form.errors.guidelines"
                                        placeholder="Any guidelines for the tenant"
                                        :class="(form.errors.guidelines)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'"
                                    >
                                    </textarea>
                                </div>
                            </div>

                            <div class="sm:col-span-6 border-b pb-0 mb-0">
                                <h3 class="text-lg font-medium text-gray-900">Home Warranty Company</h3>
                            </div>
                            <div class="sm:col-span-6 mt-[-20px]">
                                <label class="text-sm font-medium text-gray-700">Tenant may contact directly?</label>
                                <div class="mt-1 flex space-x-4">
                                    <label class="flex items-center space-x-2 cursor-pointer">
                                        <input type="radio" v-model="form.tenant_can_contact_directly" :value="true"
                                            class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500">
                                        <span class="text-sm text-gray-700">Yes</span>
                                    </label>
                                    <label class="flex items-center space-x-2 cursor-pointer">
                                        <input type="radio" v-model="form.tenant_can_contact_directly" :value="false"
                                            class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500">
                                        <span class="text-sm text-gray-700">No</span>
                                    </label>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label :class="(form.errors.warranty_company_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Company Name
                                </label>
                                <input type="text" v-model="form.warranty_company_name"
                                    :class="(form.errors.warranty_company_name)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                                <p v-text="form.errors.warranty_company_name" class="text-red-500 text-sm"></p>
                            </div>
                            <div class="sm:col-span-2">
                                <div>
                                    <label :class="(form.errors.company_phone)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">Company Phone Number</label>
                                    <input type="text" v-model="form.company_phone"
                                    :class="(form.errors.company_phone)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                                        <p v-text="form.errors.company_phone" class="text-red-500 text-sm"></p>
                                        <p class="text-gray-500 text-xs">The phone number must be 10 digits. Please remove any dashes or symbols.</p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label :class="(form.errors.company_website)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Website
                                </label>
                                <input type="text" v-model="form.company_website"
                                    placeholder="https://www.example.com"
                                    :class="(form.errors.company_website)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                                <p v-text="form.errors.company_website" class="text-red-500 text-sm"></p>
                                <p class="text-gray-500 text-xs">
                                    Please include the https:// before the URL.
                                </p>
                            </div>
                            <div class="sm:col-span-2">
                                <label :class="(form.errors.contract_number)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Contract Number
                                </label>
                                <input type="text" v-model="form.contract_number"
                                    :class="(form.errors.contract_number)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                                <p v-text="form.errors.contract_number" class="text-red-500 text-sm"></p>
                            </div>
                            <div class="sm:col-span-2">
                                <label :class="(form.errors.warranty_additional_instructions)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Additional Instructions
                                </label>
                                <textarea v-model="form.warranty_additional_instructions" rows="2"
                                    :class="(form.errors.warranty_additional_instructions)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'"
                                    placeholder="Any specific instructions for the tenant regarding the warranty company"></textarea>
                                <p v-text="form.errors.warranty_additional_instructions" class="text-red-500 text-sm"></p>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Save Button -->
                    <div class="pt-5 flex justify-end space-x-3">
                        <button 
                            type="button" 
                            @click="showConfigurationForm = false"
                            class="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400"
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            :disabled="form.processing || isSharedViewOnly"
                            class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-rent-risk-blue-400 hover:bg-rent-risk-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {{ form.processing ? 'Saving...' : 'Save Configuration' }}
                        </button>
                    </div>
                    </fieldset>
                </form>
            </div>

            <!-- Maintenance Requests List -->
            <div v-show="!showConfigurationForm">
                <!-- Filters -->
                <div class="mb-6 flex flex-wrap items-end gap-4">
                    <div class="w-28 shrink-0">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Per Page</label>
                        <select v-model="requestsForm.paginate" :disabled="isSharedViewOnly" class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md disabled:pointer-events-none disabled:opacity-50">
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div class="flex-1 min-w-[200px]">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <div class="relative">
                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <font-awesome-icon :icon="faMagnifyingGlass" class="text-gray-400 text-sm" />
                            </div>
                            <input 
                                v-model="requestsForm.search" 
                                :disabled="isSharedViewOnly"
                                type="text" 
                                placeholder="Search for maintenance requests..." 
                                class="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm disabled:pointer-events-none disabled:opacity-50"
                            >
                        </div>
                    </div>
                    <div class="flex items-center pb-0.5">
                        <SwitchGroup as="div" class="flex items-center">
                            <Switch 
                                v-model="requestsForm.trashed"
                                :disabled="isSharedViewOnly"
                                :class="[requestsForm.trashed ? 'bg-rent-risk-blue-400' : 'bg-gray-200', 'relative inline-flex h-4 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50']"
                            >
                                <span aria-hidden="true" :class="[requestsForm.trashed ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                            </Switch>
                            <SwitchLabel as="span" class="ml-3 text-sm">
                                <span class="font-medium text-gray-700">Show Archived Requests</span>
                            </SwitchLabel>
                        </SwitchGroup>
                    </div>
                </div>

                <!-- Table -->
                <div class="bg-white shadow-md rounded-lg overflow-hidden">
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Request Type
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Description
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Attachments
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Created At
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                                <tr v-for="(request, index) in maintenanceRequests?.data" :key="request.id"
                                    :class="[
                                        request.deleted_at ? 'bg-red-100' : ((index % 2 === 0) ? 'bg-white' : 'bg-gray-50'),
                                        'hover:bg-blue-50 cursor-pointer transition-colors'
                                    ]"
                                    @click="viewRequest(request)">
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <div class="flex items-center gap-3">
                                            <div class="w-8 h-8 shrink-0 rounded-full bg-blue-50 flex items-center justify-center">
                                                <font-awesome-icon :icon="faWrench" class="text-rent-risk-blue-400 text-xs" />
                                            </div>
                                            <span class="text-sm font-medium text-gray-900">{{ request.emergency_type_name || 'Other' }}</span>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 text-sm text-gray-700">
                                        {{ request.other_description || '-' }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div class="flex items-center gap-1.5">
                                            <font-awesome-icon :icon="faPaperclip" class="text-gray-400 text-xs" />
                                            <span v-if="request.images && request.images.length > 0">{{ request.images.length }} file(s)</span>
                                            <span v-else class="text-gray-400">None</span>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div class="flex items-start gap-1.5">
                                            <font-awesome-icon :icon="faCalendarAlt" class="text-gray-400 text-xs mt-0.5 shrink-0" />
                                            <div>
                                                <p>{{ request.created_at?.split(' ').slice(0, 3).join(' ') }}</p>
                                                <p class="text-xs text-gray-400">{{ request.created_at?.split(' ').slice(3).join(' ') }}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <span :class="getStatusClass(request.status)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                                            {{ request.status }}
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button 
                                            v-if="!request.deleted_at"
                                            @click.stop="viewRequest(request)"
                                            class="inline-flex items-center gap-1 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                        >
                                            View
                                            <font-awesome-icon :icon="faChevronRight" class="text-gray-400 text-xs" />
                                        </button>
                                        <div v-else class="flex justify-end gap-2">
                                            <button 
                                                @click.stop="viewRequest(request)"
                                                class="inline-flex items-center gap-1 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                                title="View Request"
                                            >
                                                View
                                                <font-awesome-icon :icon="faChevronRight" class="text-gray-400 text-xs" />
                                            </button>
                                            <button 
                                                type="button"
                                                :disabled="isSharedViewOnly"
                                                @click.stop="restoreRequest(request)"
                                                class="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-50"
                                                title="Restore Request"
                                            >
                                                Restore
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-if="!maintenanceRequests || !maintenanceRequests.data || maintenanceRequests.data.length === 0">
                                    <td colspan="6" class="px-6 py-12 text-center text-sm text-gray-500">
                                        <div class="flex flex-col items-center">
                                            <font-awesome-icon :icon="faInboxOut" class="text-gray-400 text-4xl mb-3" />
                                            <p>No maintenance requests found.</p>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination (if needed) -->
                    <div v-if="maintenanceRequests && maintenanceRequests.links" class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div class="flex-1 flex justify-between sm:hidden">
                            <a v-for="link in maintenanceRequests.links" :key="link.label" 
                               :href="link.url" 
                               :class="[link.active ? 'bg-rent-risk-blue-400 text-white' : 'bg-white text-gray-700', 'relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md hover:bg-gray-50']"
                               v-html="link.label">
                            </a>
                        </div>
                        <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p class="text-sm text-gray-700">
                                    Showing
                                    <span class="font-medium">{{ maintenanceRequests.from || 0 }}</span>
                                    to
                                    <span class="font-medium">{{ maintenanceRequests.to || 0 }}</span>
                                    of
                                    <span class="font-medium">{{ maintenanceRequests.total || 0 }}</span>
                                    results
                                </p>
                            </div>
                            <div>
                                <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                    <a v-for="link in maintenanceRequests.links" :key="link.label" 
                                       :href="link.url" 
                                       :class="[link.active ? 'z-10 bg-rent-risk-blue-400 border-rent-risk-blue-400 text-white' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50', 'relative inline-flex items-center px-4 py-2 border text-sm font-medium']"
                                       v-html="link.label">
                                    </a>
                                </nav>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Configuration Form (show if no configuration exists) -->
        <div v-else>
            <!-- Configuration Form (Always visible when no configuration exists) -->
            <form @submit.prevent="saveMaintenanceConfiguration" class="space-y-7">
            <fieldset :disabled="isSharedViewOnly" class="contents">
            <!-- Tenant Maintenance Instructions -->
            <div>
                <div class="sm:col-span-6">
                    <h3 class="text-lg leading-6 font-medium text-gray-900">
                        Tenant Maintenance Instructions
                    </h3>
                    <p class="text-xs leading-6 font-medium text-gray-500">These are the instructions your tenant will see on their dashboard once you invite them to the portal.</p>
                </div>

                <div class="mt-7 grid grid-cols-1 gap-y-12 gap-x-4 sm:grid-cols-6">
                    <div class="sm:col-span-2">
                        <label for="tenant_email"
                            :class="(form.errors.tenant_email)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            Tenant Email
                        </label>
                        <div class="mt-1 relative rounded-md">
                            <div
                                class="absolute left-0 pl-3 flex items-center pointer-events-none"
                                :class="(form.errors.tenant_email)?'top-3':'inset-y-0'">
                                <font-awesome-icon :icon="faEnvelope" class="text-gray-400" />
                            </div>
                            <input id="tenant_email" v-model="form.tenant_email" type="email"
                                :class="(form.errors.tenant_email)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'"
                            >
                            <p v-text="form.errors.tenant_email" class="text-red-500 text-sm"></p>
                        </div>
                    </div>
                    <div class="sm:col-span-2">
                        <label for="tenant_message" :class="(form.errors.tenant_message)?'block text-sm inline-flex font-medium text-red-500':'block text-sm inline-flex font-medium text-gray-700'">
                           Email sent to Tenant
                            <ToolTip
                                :title="'Email sent to Tenant'"
                                :description="'This is the message you want your tenant to receive once they put in a request. The below message is the default message.'"
                            ></ToolTip>
                        </label>
                        <div class="mt-1">
                            <textarea
                                id="tenant_message"
                                rows="4" v-model="form.tenant_message"
                                :error="form.errors.tenant_message"
                                placeholder="Custom message received by tenant.."
                                :class="(form.errors.tenant_message)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'"
                            >
                            </textarea>
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <label for="repair_amount" :class="(form.errors.repair_amount)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            Tenant is responsible for any repairs up to ($)
                        </label>
                        <div class="mt-1 relative rounded-md">
                            <input id="repair_amount" v-model="form.repair_amount" type="number" step="0.01" :class="(form.errors.repair_amount)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                            <p v-text="form.errors.repair_amount" class="text-red-500 text-sm"></p>
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <label for="specific_instructions" :class="(form.errors.specific_instructions)?'block text-sm inline-flex font-medium text-red-500':'block text-sm inline-flex font-medium text-gray-700'">
                            Specific Instructions
                            <ToolTip
                                :title="'Specific Instructions'"
                                :description="'This is where you tell your tenant how you want them to handle the maintenance request. For example, Call Home Warranty Company for plumbing or electrical issues.'"
                            ></ToolTip>
                        </label>
                        <div class="mt-1">
                            <textarea
                                id="specific_instructions"
                                rows="2" v-model="form.specific_instructions"
                                :error="form.errors.specific_instructions"
                                placeholder="Any other specific instructions for the tenant"
                                :class="(form.errors.specific_instructions)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:ring-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'"
                            >
                            </textarea>
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <label for="guidelines" :class="(form.errors.guidelines)?'block text-sm inline-flex font-medium text-red-500':'block text-sm inline-flex font-medium text-gray-700'">
                            Emergency Guidelines
                            <ToolTip
                                :title="'Emergency Guidelines'"
                                :description="'Please call landlord directly if there is any water emergency such as a leaking roof, flooding basement, etc.'"
                            ></ToolTip>
                        </label>
                        <div class="mt-1">
                            <textarea
                                id="guidelines"
                                rows="2" v-model="form.guidelines"
                                :error="form.errors.guidelines"
                                placeholder="Any guidelines for the tenant"
                                :class="(form.errors.guidelines)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'"
                            >
                            </textarea>
                        </div>
                    </div>

                    <div class="sm:col-span-6 border-b pb-0 mb-0">
                        <h3 class="text-lg font-medium text-gray-900">Home Warranty Company</h3>
                    </div>
                    <div class="sm:col-span-6 mt-[-20px]">
                        <label class="text-sm font-medium text-gray-700">Tenant may contact directly?</label>
                        <div class="mt-1 flex space-x-4">
                            <label class="flex items-center space-x-2 cursor-pointer">
                                <input type="radio" v-model="form.tenant_can_contact_directly" :value="true"
                                    class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500">
                                <span class="text-sm text-gray-700">Yes</span>
                            </label>
                            <label class="flex items-center space-x-2 cursor-pointer">
                                <input type="radio" v-model="form.tenant_can_contact_directly" :value="false"
                                    class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500">
                                <span class="text-sm text-gray-700">No</span>
                            </label>
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <label :class="(form.errors.warranty_company_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            Company Name
                        </label>
                        <input type="text" v-model="form.warranty_company_name"
                            :class="(form.errors.warranty_company_name)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                        <p v-text="form.errors.warranty_company_name" class="text-red-500 text-sm"></p>
                    </div>
                    <div class="sm:col-span-2">
                        <div>
                            <label :class="(form.errors.company_phone)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">Company Phone Number</label>
                            <input type="text" v-model="form.company_phone"
                            :class="(form.errors.company_phone)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                                <p v-text="form.errors.company_phone" class="text-red-500 text-sm"></p>
                                <p class="text-gray-500 text-xs">The phone number must be 10 digits. Please remove any dashes or symbols.</p>
                        </div>
                    </div>
                    <div class="sm:col-span-2">
                        <label :class="(form.errors.company_website)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            Website
                        </label>
                        <input type="text" v-model="form.company_website"
                            placeholder="https://www.example.com"
                            :class="(form.errors.company_website)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                        <p v-text="form.errors.company_website" class="text-red-500 text-sm"></p>
                        <p class="text-gray-500 text-xs">
                            Please include the https:// before the URL.
                        </p>
                    </div>
                    <div class="sm:col-span-2">
                        <label :class="(form.errors.contract_number)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            Contract Number
                        </label>
                        <input type="text" v-model="form.contract_number"
                            :class="(form.errors.contract_number)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                        <p v-text="form.errors.contract_number" class="text-red-500 text-sm"></p>
                    </div>
                    <div class="sm:col-span-2">
                        <label :class="(form.errors.warranty_additional_instructions)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            Additional Instructions
                        </label>
                        <textarea v-model="form.warranty_additional_instructions" rows="2"
                            :class="(form.errors.warranty_additional_instructions)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'"
                            placeholder="Any specific instructions for the tenant regarding the warranty company"></textarea>
                        <p v-text="form.errors.warranty_additional_instructions" class="text-red-500 text-sm"></p>
                    </div>
                </div>
            </div>
            
            </fieldset>
        </form>

        <!-- Terms & Conditions Section -->
        <div v-if="subscriptionData.has_access && !subscriptionData.is_terms_accepted" class="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
            <h3 class="text-lg font-medium text-blue-800 mb-4">Terms and Conditions</h3>
            <p class="text-sm text-blue-700 mb-4">You must accept the Terms and Conditions to use the maintenance module.</p>
            
            <div class="mb-4">
                <label class="flex items-start">
                    <input 
                        type="checkbox" 
                        v-model="maintenanceTermsAccepted"
                        :disabled="isSharedViewOnly"
                        class="mt-1 h-4 w-4 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400 border-gray-300 rounded disabled:opacity-50"
                    />
                    <span class="ml-2 text-sm text-gray-700">
                        I agree to the <a target="_blank" :href="appUrl('/terms-of-service')" class="text-rent-risk-blue-400 underline">Terms and Conditions</a> for the maintenance module
                    </span>
                </label>
            </div>

            <p v-if="termsError" class="text-sm text-red-600 mb-4">{{ termsError }}</p>

            <div class="flex justify-end">
                <button
                    type="button"
                    @click="acceptMaintenanceTerms"
                    :disabled="!maintenanceTermsAccepted || acceptingTerms || isSharedViewOnly"
                    class="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {{ acceptingTerms ? 'Accepting...' : 'Accept Terms' }}
                </button>
            </div>
        </div>

        <!-- 86bcd9hg3 (decision 18): maintenance is part of RiskGuard Pro, not sold on its own. -->
        <div v-if="!subscriptionData.has_access" class="mt-6 rounded-lg border border-[#e0cb85] bg-[#fbf6e3] p-6" data-test="maintenance-pro-card">
            <div class="flex items-start gap-3">
                <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white">
                    <font-awesome-icon :icon="faWrench" class="text-[#7d6310]" />
                </div>
                <div>
                    <h3 class="text-base font-semibold text-gray-900">Maintenance requests are part of RiskGuard Pro</h3>
                    <p class="mt-1 text-sm text-gray-700">Upgrade your account to Pro to accept maintenance requests on every property, along with rent payments, leases and more.</p>
                </div>
            </div>
            <button
                type="button"
                :disabled="isSharedViewOnly"
                class="mt-4 inline-flex items-center gap-2 rounded-md bg-gold-500 px-4 py-2 text-sm font-bold text-[#7d6310] hover:bg-gold-700 disabled:pointer-events-none disabled:opacity-50"
                @click="emit('request-upgrade')"
            >
                Upgrade to Pro
            </button>
        </div>

        <!-- Success State -->
        <div v-if="subscriptionData.has_access && subscriptionData.is_terms_accepted" class="bg-green-50 border border-green-200 rounded-lg p-4 mt-6">
            <div class="flex">
                <div class="flex-shrink-0">
                    <font-awesome-icon :icon="faCheckCircle" class="h-5 w-5 text-green-400" />
                </div>
                <div class="ml-3">
                    <p class="text-sm font-medium text-green-800">
                        <template v-if="subscriptionData.access_via_riskguard">
                            You have accepted the terms and this property is covered by your {{ subscriptionData.is_riskguard_pro ? 'RiskGuard Pro' : 'RiskGuard' }} subscription. You can now save your maintenance configuration.
                        </template>
                        <template v-else>
                            You have accepted the terms and have an active maintenance subscription. You can now save your maintenance configuration.
                        </template>
                    </p>
                </div>
            </div>
        </div>

        <!-- Save Configuration Button -->
        <div class="pt-5 flex justify-end">
            <button 
                type="button"
                @click="saveMaintenanceConfiguration"
                :disabled="form.processing || !subscriptionData.has_access || !subscriptionData.is_terms_accepted || isSharedViewOnly"
                class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-rent-risk-blue-400 hover:bg-rent-risk-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {{ form.processing ? 'Saving...' : 'Save Configuration' }}
            </button>
        </div>
        </div>

        <!-- Restore Modal -->
        <TransitionRoot as="template" :show="showRestoreModal">
            <Dialog as="div" class="relative z-50" @close="showRestoreModal = false">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                <div>
                                    <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                        <font-awesome-icon :icon="faArrowsRotate" class="h-6 w-6 text-green-500" aria-hidden="true" />
                                    </div>
                                    <div class="mt-3 text-center sm:mt-5">
                                        <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to restore this request?</DialogTitle>
                                        <div class="mt-2">
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-5 sm:mt-6 flex">
                                    <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showRestoreModal = false">Cancel</button>
                                    <button type="button" class="inline-flex w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white bg-rent-risk-blue-400 hover:bg-rent-risk-blue-500 focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="restoreSelectedRequest">Confirm</button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>

    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useForm, router } from '@inertiajs/vue3'
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { 
    faEnvelope, 
    faCalendarAlt, 
    faCheckCircle,
    faWrench,
    faPencil,
    faEye,
    faInboxOut,
    faClock,
    faBoxArchive,
    faChevronRight,
    faMagnifyingGlass,
    faPaperclip,
} from '@fortawesome/pro-solid-svg-icons'
import { faArrowsRotate, faBell } from '@fortawesome/pro-light-svg-icons'
import { Switch, SwitchGroup, SwitchLabel, Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import ToolTip from '@/Shared/ToolTip.vue'
import HowItWorksBanner from '@/Pages/V2/Properties/Components/HowItWorksBanner.vue'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

// Route function is available globally via Ziggy
const route = (typeof window !== 'undefined' && window.route) || ((name, ...params) => {
    console.warn('Route function not available')
    return '#'
})

const props = defineProps({
    property: {
        type: Object,
        required: true
    },
    tab: {
        type: String,
        default: 'maintenance'
    },
    tabData: {
        type: Object,
        default: () => ({})
    },
    subscriptionProtectionRef: {
        type: Object,
        default: null
    },
    propertyAccess: {
        type: String,
        default: 'owner',
    },
    viewOnlyTooltip: {
        type: Object,
        default: null,
    },
    isScreeningOnly: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['request-upgrade'])

const isSharedViewOnly = computed(() => props.propertyAccess === 'shared_view')

// Subscription data
const subscriptionData = computed(() => props.tabData?.subscription_data || {})
const maintenanceTenantProperty = computed(() => props.tabData?.maintenance_tenant_property)
const maintenanceRequests = computed(() => props.tabData?.maintenance_requests)
const filters = computed(() => props.tabData?.filters)
const requestStats = computed(() => props.tabData?.request_stats || { total: 0, in_progress: 0, completed: 0, archived: 0 })

const howItWorksSteps = [
    {
        icon: faInboxOut,
        title: 'Tenant submits a request',
        description: 'You\'ll see the issue and photos.',
    },
    {
        icon: faBell,
        title: 'You\'re notified',
        description: 'In app chats & email updates.',
    },
    {
        icon: faWrench,
        title: 'Resolve the Issue',
        description: 'Assign vendors for the ticket.',
    },
    {
        icon: faBoxArchive,
        title: 'Review history',
        description: 'View and download records anytime.',
    },
]

// CTA label: "Invite Tenant" before a tenant email is set, "Edit Maintenance" after
const hasTenantInvited = computed(() => {
    const email = maintenanceTenantProperty.value?.tenant_email
    return !!(email && email.trim())
})

// Check if configuration exists
const hasConfiguration = computed(() => !!maintenanceTenantProperty.value)
const canViewRequests = computed(() => {
    if (!hasConfiguration.value) {
        return false
    }

    if (isSharedViewOnly.value) {
        return true
    }

    return Boolean(subscriptionData.value?.has_access && subscriptionData.value?.is_terms_accepted)
})

// Toggle state for showing configuration form when editing
const showConfigurationForm = ref(false)

// Form state
const form = useForm({
    tenant_email: maintenanceTenantProperty.value?.tenant_email || '',
    tenant_message: maintenanceTenantProperty.value?.tenant_message || "Your request has been received and will be responded to in 24 hours. If this is a maintenance emergency, please call xxx-xxx-xxxx. If you are in danger, please call 911.",
    repair_amount: maintenanceTenantProperty.value?.repair_amount || null,
    specific_instructions: maintenanceTenantProperty.value?.specific_instructions || '',
    guidelines: maintenanceTenantProperty.value?.guidelines || '',
    tenant_can_contact_directly: maintenanceTenantProperty.value?.tenant_can_contact_directly ?? null,
    warranty_company_name: maintenanceTenantProperty.value?.warranty_company_name || '',
    company_phone: maintenanceTenantProperty.value?.company_phone || '',
    company_website: maintenanceTenantProperty.value?.company_website || '',
    contract_number: maintenanceTenantProperty.value?.contract_number || '',
    warranty_additional_instructions: maintenanceTenantProperty.value?.warranty_additional_instructions || '',
})

// Requests form for filters
const requestsForm = ref({
    search: filters.value?.search || '',
    paginate: filters.value?.paginate || 25,
    sort_by: filters.value?.sort_by || '',
    sort_direction: filters.value?.sort_direction || 'desc',
    trashed: filters.value?.trashed || false,
})

// Watch for filter changes and reload
watch(() => requestsForm.value, throttle(() => {
    if (isSharedViewOnly.value) {
        return
    }
    if (hasConfiguration.value) {
        router.get(
            route('properties.show', props.property.id), 
            { tab: 'maintenance', ...pickBy(requestsForm.value) }, 
            { preserveState: true, preserveScroll: true }
        )
    }
}, 150), { deep: true })

// Terms acceptance
const maintenanceTermsAccepted = ref(false)
const acceptingTerms = ref(false)
const termsError = ref(null)

// Subscription state (a Pro purchase started from this tab runs in Show.vue)
const isSubscribing = ref(false)

// Restore modal state
const showRestoreModal = ref(false)
const selectedRequest = ref(null)

// Watch for maintenance data changes and update form
watch(() => maintenanceTenantProperty.value, (newData) => {
    if (newData) {
        form.tenant_email = newData.tenant_email || ''
        form.tenant_message = newData.tenant_message || "Your request has been received and will be responded to in 24 hours. If this is a maintenance emergency, please call xxx-xxx-xxxx. If you are in danger, please call 911."
        form.repair_amount = newData.repair_amount || null
        form.specific_instructions = newData.specific_instructions || ''
        form.guidelines = newData.guidelines || ''
        form.tenant_can_contact_directly = newData.tenant_can_contact_directly ?? null
        form.warranty_company_name = newData.warranty_company_name || ''
        form.company_phone = newData.company_phone || ''
        form.company_website = newData.company_website || ''
        form.contract_number = newData.contract_number || ''
        form.warranty_additional_instructions = newData.warranty_additional_instructions || ''
    }
}, { immediate: true })

// Methods
const acceptMaintenanceTerms = () => {
    if (acceptingTerms.value || isSharedViewOnly.value) return

    acceptingTerms.value = true
    termsError.value = null

    // axios instead of form.post / router.post: Inertia v2's post variants return
    // void, so network failures (ERR_NETWORK, CORS, timeout) bubble to
    // onunhandledrejection instead of onError. Same compliance / audit-trail
    // concern as RAC-9963 — silent failure leaves no server-side ToS record.
    axios.post(route('maintenance.accept-terms-v2'))
        .then(() => {
            acceptingTerms.value = false
            termsError.value = null
            // Reload to get updated subscription data
            router.reload({ only: ['tabData'] })
        })
        .catch((error) => {
            acceptingTerms.value = false
            if (error.response && error.response.data && (error.response.data.message || error.response.data.error)) {
                termsError.value = error.response.data.message || error.response.data.error
            } else if (error.request) {
                termsError.value = 'Network error — please check your connection and tap Accept again.'
            } else {
                termsError.value = 'Failed to accept terms. Please try again.'
            }
        })
}

const saveMaintenanceConfiguration = () => {
    if (isSharedViewOnly.value) return
    form.post(route('properties.maintenance.configure', props.property.id), {
        preserveScroll: true,
        onSuccess: () => {
            showConfigurationForm.value = false
        }
    })
}

const viewRequest = (request) => {
    router.visit(route('maintenance-requests.show', [props.property.id, request.id]))
}

const restoreRequest = (request) => {
    if (isSharedViewOnly.value) return
    selectedRequest.value = request
    showRestoreModal.value = true
}

const restoreSelectedRequest = () => {
    if (isSharedViewOnly.value) return
    if (!selectedRequest.value) return
    
    router.put(
        route('maintenance-requests.restore', {
            maintenance_property: selectedRequest.value.property_id,
            maintenance_request: selectedRequest.value.id
        }), 
        {},
        {
            onFinish: () => {
                showRestoreModal.value = false
                selectedRequest.value = null
            }
        }
    )
}

const getStatusClass = (status) => {
    const statusClasses = {
        'Submitted': 'bg-blue-100 text-blue-800',
        'In progress': 'bg-orange-100 text-orange-700',
        'In Progress': 'bg-orange-100 text-orange-700',
        'Completed': 'bg-green-100 text-green-800',
        'Closed': 'bg-gray-100 text-gray-600',
        'Cancelled': 'bg-red-100 text-red-800',
    }
    return statusClasses[status] || 'bg-gray-100 text-gray-600'
}

// Handle subscription success from parent's SubscriptionProtection component
const handleSubscriptionSuccess = (data) => {
    isSubscribing.value = false
    // Reload to get updated subscription data
    router.reload({ only: ['tabData'] })
}

const handleSubscriptionError = (data) => {
    isSubscribing.value = false
    alert(data?.error || 'Subscription failed. Please try again.')
}

const handleSubscriptionCancel = () => {
    isSubscribing.value = false
}

// Expose methods to parent
defineExpose({
    handleSubscriptionSuccess,
    handleSubscriptionError,
    handleSubscriptionCancel,
})
</script>
